import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronDown, ChevronUp, Info, Shield, AlertTriangle } from 'lucide-react';
import { getDrugComparison } from '../services/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const scoreDescriptions = {
  efficacy: 'Based on clinical trial data, bioequivalence studies, and therapeutic equivalence ratings',
  regulatory: 'Regulatory approval status across target markets, FDA/EMA designations, and compliance history',
  availability: 'Current supply status, inventory levels, and procurement accessibility',
  distribution: 'Geographic reach, logistics network, and delivery reliability',
  manufacturing: 'Manufacturer credibility, GMP compliance, and quality track record',
  study_support: 'Quantity and quality of supporting clinical evidence and publications',
  procurement_fit: 'Alignment with hospital procurement requirements and operational feasibility'
};

function ScoreCell({ score, label, expanded, onToggle }) {
  const getColor = (s) => {
    if (s >= 85) return 'text-emerald-600 bg-emerald-50';
    if (s >= 70) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getBarColor = (s) => {
    if (s >= 85) return 'bg-emerald-500';
    if (s >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            onClick={onToggle}
            className={`w-full text-center py-2 px-3 rounded-lg transition-colors ${getColor(score)} hover:opacity-80`}
          >
            <span className="font-semibold">{score}</span>
            {expanded ? (
              <ChevronUp className="w-3 h-3 inline ml-1" />
            ) : (
              <ChevronDown className="w-3 h-3 inline ml-1" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm">{scoreDescriptions[label] || `Score based on ${label} assessment`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ExpandedScoreRow({ generic, weights }) {
  return (
    <tr className="bg-slate-50 animate-fade-in">
      <td colSpan="10" className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Score Breakdown</h4>
            <div className="space-y-3">
              {Object.entries(generic.scores || {}).map(([key, value]) => (
                <div key={key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                    <span className="text-slate-500">
                      {value} × {weights[key] || 10}% = {((value * (weights[key] || 10)) / 100).toFixed(1)}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full">
                    <div 
                      className={`h-full rounded-full ${value >= 85 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Additional Details</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Regulatory Status</span>
                <span className="text-slate-900">{generic.regulatory_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Supply Status</span>
                <span className="text-slate-900">{generic.supply_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Bioequivalence</span>
                <span className="text-slate-900">{generic.bioequivalence || 'Not specified'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Price (USD)</span>
                <span className="text-slate-900">${generic.price_usd || 'N/A'}</span>
              </div>
            </div>
            
            {generic.risk_flags?.filter(Boolean).length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Risk Flags
                </div>
                <ul className="space-y-1">
                  {generic.risk_flags.filter(Boolean).map((flag, idx) => (
                    <li key={idx} className="text-sm text-amber-600">• {flag}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function ComparisonPage() {
  const { drugId } = useParams();
  const [drug, setDrug] = useState(null);
  const [generics, setGenerics] = useState([]);
  const [weights, setWeights] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getDrugComparison(drugId);
        setDrug(res.data.drug);
        setGenerics(res.data.generics || []);
        setWeights(res.data.weights || {});
      } catch (error) {
        console.error('Failed to fetch comparison data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [drugId]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      'recommended': { class: 'badge-success', text: 'Recommended' },
      'pharmacist_review': { class: 'badge-warning', text: 'Pharmacist Review' },
      'clinical_review': { class: 'badge-danger', text: 'Clinical Review' },
      'not_recommended': { class: 'badge-neutral', text: 'Not Recommended' }
    };
    return badges[status] || { class: 'badge-neutral', text: status };
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="comparison-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="comparison-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-slate-700">Dashboard</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to={`/drug/${drugId}`} className="hover:text-slate-700">{drug?.brand_name}</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900">Comparison</span>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">
          Generic Comparison: {drug?.brand_name}
        </h1>
        <p className="text-slate-500">
          Comparing {generics.length} generic alternatives for {drug?.generic_name}
        </p>
      </div>

      {/* Weights Info */}
      <div className="card-premium p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-slate-900">Scoring Weights</h3>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="text-slate-600">Efficacy: <span className="font-medium">{weights.efficacy || 25}%</span></span>
          <span className="text-slate-600">Regulatory: <span className="font-medium">{weights.regulatory || 20}%</span></span>
          <span className="text-slate-600">Availability: <span className="font-medium">{weights.availability || 15}%</span></span>
          <span className="text-slate-600">Distribution: <span className="font-medium">{weights.distribution || 10}%</span></span>
          <span className="text-slate-600">Manufacturing: <span className="font-medium">{weights.manufacturing || 10}%</span></span>
          <span className="text-slate-600">Study Support: <span className="font-medium">{weights.study_support || 10}%</span></span>
          <span className="text-slate-600">Procurement Fit: <span className="font-medium">{weights.procurement_fit || 10}%</span></span>
        </div>
        <Link to="/admin" className="text-sm text-[#0A2540] hover:underline mt-2 inline-block">
          Adjust weights
        </Link>
      </div>

      {/* Comparison Table */}
      <div className="card-premium overflow-hidden" data-testid="comparison-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="data-table-header text-left">Rank</th>
                <th className="data-table-header text-left">Generic</th>
                <th className="data-table-header text-left">Manufacturer</th>
                <th className="data-table-header text-center">Efficacy</th>
                <th className="data-table-header text-center">Regulatory</th>
                <th className="data-table-header text-center">Availability</th>
                <th className="data-table-header text-center">Overall</th>
                <th className="data-table-header text-center">Confidence</th>
                <th className="data-table-header text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {generics.map((generic, idx) => {
                const badge = getStatusBadge(generic.review_status);
                return (
                  <React.Fragment key={generic.id}>
                    <tr 
                      className={`data-table-row cursor-pointer ${expandedRows[generic.id] ? 'bg-slate-50' : ''}`}
                      onClick={() => toggleRow(generic.id)}
                      data-testid={`comparison-row-${generic.id}`}
                    >
                      <td className="data-table-cell">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
                          #{idx + 1}
                        </div>
                      </td>
                      <td className="data-table-cell">
                        <div>
                          <p className="font-medium text-slate-900">{generic.name}</p>
                          <p className="text-xs text-slate-500">{generic.dosage_form} • {generic.strength}</p>
                        </div>
                      </td>
                      <td className="data-table-cell">
                        <span className="text-slate-700">{generic.manufacturer}</span>
                      </td>
                      <td className="data-table-cell">
                        <ScoreCell 
                          score={generic.scores?.efficacy || 0} 
                          label="efficacy"
                          expanded={expandedRows[generic.id]}
                          onToggle={(e) => { e.stopPropagation(); toggleRow(generic.id); }}
                        />
                      </td>
                      <td className="data-table-cell">
                        <ScoreCell 
                          score={generic.scores?.regulatory || 0} 
                          label="regulatory"
                          expanded={expandedRows[generic.id]}
                          onToggle={(e) => { e.stopPropagation(); toggleRow(generic.id); }}
                        />
                      </td>
                      <td className="data-table-cell">
                        <ScoreCell 
                          score={generic.scores?.availability || 0} 
                          label="availability"
                          expanded={expandedRows[generic.id]}
                          onToggle={(e) => { e.stopPropagation(); toggleRow(generic.id); }}
                        />
                      </td>
                      <td className="data-table-cell">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-slate-900">{generic.overall_score}</span>
                          <span className="text-slate-400 text-sm">/100</span>
                        </div>
                      </td>
                      <td className="data-table-cell text-center">
                        <div className="w-16 mx-auto">
                          <div className="h-2 bg-slate-200 rounded-full mb-1">
                            <div 
                              className={`h-full rounded-full ${
                                generic.confidence >= 85 ? 'bg-emerald-500' : 
                                generic.confidence >= 70 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${generic.confidence}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">{generic.confidence}%</span>
                        </div>
                      </td>
                      <td className="data-table-cell text-center">
                        <span className={badge.class}>{badge.text}</span>
                      </td>
                    </tr>
                    {expandedRows[generic.id] && (
                      <ExpandedScoreRow generic={generic} weights={weights} />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {generics.length === 0 && (
          <div className="py-12 text-center">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No generic alternatives available for comparison</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center gap-6 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span>High confidence (≥85)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span>Moderate confidence (70-84)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span>Low confidence (&lt;70)</span>
        </div>
      </div>
    </div>
  );
}
